import React, { useState, useEffect, forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import * as purchaseService from '../../../services/purchaseService';
import SearchableSupplierSelect from '../bills/SearchableSupplierSelect';
import SearchableItemSelect from '../invoices/SearchableItemSelect';
import SimpleDropdown from '../invoices/SimpleDropdown';
import PurchasePdfDocument from './PurchasePdfDocument';
import { pdf } from '@react-pdf/renderer';
import { getOrgId } from '../../../services/orgStore';
import {
  calculateLineTotal,
  calculateLineTax,
  calculatePurchaseTotals,
  getTaxRate,
} from './purchaseCalculations';

/* ---- Date helpers ---- */
const parseYmd = (ymd) => {
  if (!ymd) return null;
  const d = new Date(`${ymd}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatYmd = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
  </svg>
  );

const DateInput = forwardRef(({ value, onClick, placeholder }, ref) => (
  <div className="relative">
    <input
      ref={ref}
      value={value || ''}
      onClick={onClick}
      readOnly
      placeholder={placeholder}
      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded cursor-pointer font-[Arial] focus:outline-none focus:ring-1 focus:ring-blue-500"
    />
    <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
      <CalendarIcon />
    </span>
  </div>
));

const PopperContainer = ({ children }) => <div className="z-[11000]">{children}</div>;

/* ---- Defaults ---- */
const emptyLine = {
  item_id: '', item_description: '', quantity: 1,
  unit_price: 0, total_price: 0, tax_rate_id: 'GST', gst_rate: 0.1,
};

/** Compute next PO number (e.g. PO-01, PO-02) from existing purchases. */
const getNextPurchaseNumber = (purchases) => {
  let maxNum = 0;
  (purchases || []).forEach((p) => {
    const m = (p.purchase_number || '').match(/^PO-(\d+)$/i);
    if (m) maxNum = Math.max(maxNum, parseInt(m[1], 10));
  });
  return `PO-${String(maxNum + 1).padStart(2, '0')}`;
};

const defaultFormData = (purchases = []) => ({
  supplier_id: '',
  supplier_name: '',
  purchase_number: getNextPurchaseNumber(purchases),
  reference: '',
  purchase_order_date: formatYmd(new Date()),
  due_date: '',
  tax_inclusion: 'tax_exclusive',
  ship_to: '',
  telephone: '',
  contact_name: '',
  supplier_email: '',
  note_to_supplier: '',
});

/* ================================================================ */

export default function PurchaseModal({
  isOpen, onClose, onSubmit, initialData,
  draftToRestore, onClearDraftRestore,
  suppliers = [], items = [], taxes = [],
  setup = [], setup2 = [], onPreview,
  purchases = [], onImportToBill,
  onCreateSupplier,
  createdSupplierId,
}) {
  const [formData, setFormData] = useState(defaultFormData());
  const [lineItems, setLineItems] = useState([{ ...emptyLine, id: Date.now() }]);

  /* ---- Initialise / load ---- */
  useEffect(() => {
    if (!isOpen) return;

    if (draftToRestore) {
      setFormData(draftToRestore.formData);
      setLineItems(draftToRestore.lineItems || [{ ...emptyLine, id: Date.now() }]);
      onClearDraftRestore?.();
      return;
    }

    if (initialData) {
      setFormData({
        supplier_id: initialData.supplier_id || '',
        supplier_name: initialData.supplier_name || initialData.supplier?.supplier_name || '',
        purchase_number: initialData.purchase_number || '',
        reference: initialData.reference || '',
        purchase_order_date: initialData.purchase_order_date?.split('T')[0] || formatYmd(new Date()),
        due_date: initialData.due_date?.split('T')[0] || '',
        tax_inclusion: (() => {
          const t = initialData.line_amount_types;
          if (t === 'inclusive') return 'tax_inclusive';
          if (t === 'exclusive') return 'tax_exclusive';
          if (t === 'none') return 'no_tax';
          return 'tax_exclusive';
        })(),
        ship_to: initialData.ship_to || '',
        telephone: initialData.telephone || '',
        contact_name: initialData.contact_name || '',
        supplier_email: initialData.supplier_email || '',
        note_to_supplier: initialData.note_to_supplier || '',
      });

      (async () => {
        const data = await purchaseService.fetchPurchaseLines(initialData.purchase_order_id);
        if (data?.length) {
          setLineItems(data.map((l, idx) => ({
            id: l.purchase_order_line_id || Date.now() + idx,
            item_id: l.item_id || '',
            item_description: l.item_description || '',
            quantity: l.qty || 1,
            unit_price: l.unit_price || 0,
            total_price: l.line_total_inclusive_tax || 0,
            tax_rate_id: l.tax_rate_id || 'GST',
            gst_rate: 0.1,
          })));
        } else {
          setLineItems([{ ...emptyLine, id: Date.now() }]);
        }
      })();
    } else {
      setFormData(defaultFormData(purchases));
      setLineItems([{ ...emptyLine, id: Date.now() }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialData]);

  useEffect(() => {
    if (!isOpen) return;
    if (!createdSupplierId) return;
    // Only auto-fill after creating a supplier for a NEW purchase order.
    if (initialData) return;
    const s = suppliers.find((x) => x.supplier_id?.toString() === createdSupplierId?.toString());
    if (!s) return;
    const address = [s.address || s.street, s.city, s.state, s.postcode].filter(Boolean).join(', ');
    setFormData((prev) => ({
      ...prev,
      supplier_id: s.supplier_id?.toString?.() ?? '',
      supplier_name: s.supplier_name || '',
      ship_to: prev.ship_to || address || '',
      telephone: s.contact_number || s.telephone || prev.telephone || '',
      contact_name: s.contact_name || prev.contact_name || '',
      supplier_email: s.email || prev.supplier_email || '',
    }));
  }, [isOpen, createdSupplierId, suppliers, initialData]);

  /* ---- Handlers ---- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'tax_inclusion') {
      setLineItems((prev) =>
        prev.map((item) => {
          const rate = getTaxRate(item.tax_rate_id || 'GST', taxes);
          return { ...item, total_price: calculateLineTotal(item.quantity || 0, item.unit_price || 0, rate, value) };
        })
      );
    }
  };

  const handleLineChange = (id, field, val, selectedProduct = null) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const updated = { ...item, [field]: val };

        if (field === 'item_id') {
          const product = selectedProduct || items.find((i) => i.item_id === val);
          if (product) {
            updated.item_id = val;
            updated.item_description = product.sales_description || product.item_description || '';
            // Use cost for purchase orders (bills/invoices use selling price)
            updated.unit_price = parseFloat(product.cost_excl) || 0;
            if (product.sales_tax_code) updated.tax_rate_id = product.sales_tax_code;
          }
        }

        const qty = field === 'quantity' ? parseFloat(val) || 0 : updated.quantity || 1;
        const price = field === 'unit_price' ? parseFloat(val) || 0 : updated.unit_price;
        const rate = getTaxRate(field === 'tax_rate_id' ? val : updated.tax_rate_id || 'GST', taxes);
        updated.total_price = calculateLineTotal(qty, price, rate, formData.tax_inclusion);

        return updated;
      })
    );
  };

  const addLine = () => setLineItems([...lineItems, { ...emptyLine, id: Date.now() }]);
  const removeLine = (id) => { if (lineItems.length > 1) setLineItems(lineItems.filter((l) => l.id !== id)); };
  const clearLines = () => setLineItems([{ ...emptyLine, id: Date.now() }]);

  const { subtotal, taxTotal, grandTotal } = calculatePurchaseTotals(lineItems, taxes, formData.tax_inclusion);

  /* ---- Build draft object (shared by preview) ---- */
  const buildDraft = () => {
    const supplier = suppliers.find((s) => s.supplier_id?.toString() === formData.supplier_id?.toString());
    return {
      formData: {
        ...formData,
        ...(initialData?.purchase_order_id != null ? { purchase_order_id: initialData.purchase_order_id } : {}),
        supplier_name: supplier?.supplier_name || '',
      },
      company: Array.isArray(setup) ? setup[0] : setup,
      supplier: supplier,
      lineItems, subtotal, taxTotal, grandTotal, taxes, setup2,
    };
  };

  const handlePreview = () => onPreview?.(buildDraft());
  
  const handleSend = async () => {
    const toEmail = formData.supplier_email?.trim();
    if (!toEmail) { alert('Please enter a valid email address before sending'); return; }

    const supplier = suppliers.find((s) => s.supplier_id?.toString() === formData.supplier_id?.toString());
    const payload = {
      toEmail,
      purchase_order_id: initialData?.purchase_order_id ?? null,
      purchase_number: formData.purchase_number || null,
      supplierName: supplier?.supplier_name || null,
      grandTotal: grandTotal.toFixed(2), dueDate: formData.due_date || null,
      org_id: getOrgId(),
    };

    try {
      const draft = buildDraft();
      const pdfBlob = await pdf(<PurchasePdfDocument draftPurchase={draft} />).toBlob();
      payload.pdfBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(pdfBlob);
      });
      const res = await fetch('/api/send/purchase', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.details || 'Failed to send');
      alert('Purchase order sent successfully.');
      onClose();
    } catch (err) {
      console.error('handleSend error:', err);
      alert('Failed to send: ' + (err.message || String(err)));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.supplier_id) {
      alert('Supplier is required.');
      return;
    }

    if (!formData.purchase_number?.trim()) {
      alert('Order number is required.');
      return;
    }

    const editingId = initialData?.purchase_order_id;
    const duplicate = purchases.find(
      (p) => p.purchase_number?.toString().trim() === formData.purchase_number.trim() && p.purchase_order_id !== editingId
    );
    if (duplicate) {
      alert(`Order number "${formData.purchase_number.trim()}" is already used by an existing purchase order.`);
      return;
    }

    const { tax_inclusion, ...dbData } = formData;
    const lineAmountTypes = tax_inclusion === 'tax_inclusive' ? 'inclusive' : tax_inclusion === 'tax_exclusive' ? 'exclusive' : 'none';

    onSubmit({
      header: {
        ...dbData,
        line_amount_types: lineAmountTypes,
        total_exclusive_tax: subtotal,
        tax_amount: taxTotal,
        total_amount: grandTotal,
        purchase_created_date: initialData?.purchase_created_date || new Date().toISOString(),
      },
      lines: lineItems.map(({ id, gst_rate, ...rest }) => rest),
    });
  };

  const handleImportToBill = () => {
    const today = formatYmd(new Date());
    const billFormData = {
      supplier_id: formData.supplier_id,
      supplier_name: formData.supplier_name,
      supplier_email: formData.supplier_email,
      bill_number: '',
      reference: formData.reference,
      bill_date: today,
      due_date: formData.due_date || today,
      tax_inclusion: formData.tax_inclusion,
      note_to_supplier: formData.note_to_supplier,
      note_for_bill_hidden: formData.note_for_bill_hidden,
    };
    onImportToBill?.({ formData: billFormData, lineItems });
    onClose();
  };

  if (!isOpen) return null;

  /* ================================================================ */
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]">
      <div className="bg-white w-[90%] max-w-[1500px] h-[95vh] rounded-lg flex flex-col overflow-hidden shadow-xl">

        {/* Header */}
        <div className="px-8 py-5 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold font-[Arial]">
            {initialData ? 'Edit Purchase Order' : 'Create Purchase Order'}
          </h2>
          <button onClick={onClose} className="text-2xl text-gray-400 bg-transparent border-none cursor-pointer hover:text-gray-600">×</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-x-auto overflow-y-auto p-8 bg-gray-50">
          <form id="purchaseForm" onSubmit={handleSubmit}
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>

            {/* Header Fields */}
            <div className="mb-10 font-[Arial]">
              <div className="grid grid-cols-[210px_180px_180px_200px_220px_1fr] gap-4 mb-4 items-end">
                <div>
                  <label className="block text-[13px] text-gray-700 font-medium mb-1">Contact (Supplier)</label>
                  <SearchableSupplierSelect
                    value={formData.supplier_id}
                    suppliers={suppliers}
                    onSelect={(s) => {
                      const address = [s.address || s.street, s.city, s.state, s.postcode].filter(Boolean).join(', ');
                      setFormData((prev) => ({
                        ...prev,
                        supplier_id: s.supplier_id.toString(),
                        supplier_name: s.supplier_name || '',
                        ship_to: prev.ship_to || address || '',
                        telephone: s.contact_number || s.telephone || prev.telephone || '',
                        contact_name: s.contact_name || prev.contact_name || '',
                        supplier_email: s.email || prev.supplier_email || '',
                      }));
                    }}
                    onCreateSupplier={onCreateSupplier}
                  />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 font-medium mb-1">Date raised</label>
                  <DatePicker selected={parseYmd(formData.purchase_order_date)} onChange={(d) => handleChange({ target: { name: 'purchase_order_date', value: formatYmd(d) } })}
                    dateFormat="d MMM yyyy" placeholderText="Select date" showPopperArrow={false} popperContainer={PopperContainer} customInput={<DateInput />}
                    showMonthDropdown showYearDropdown scrollableYearDropdown yearDropdownItemNumber={10} calendarStartDay={1} popperClassName="custom-datepicker" />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 font-medium mb-1">Delivery date</label>
                  <DatePicker selected={parseYmd(formData.due_date)} onChange={(d) => handleChange({ target: { name: 'due_date', value: formatYmd(d) } })}
                    dateFormat="d MMM yyyy" placeholderText="Select date" showPopperArrow={false} popperContainer={PopperContainer} customInput={<DateInput />}
                    showMonthDropdown showYearDropdown scrollableYearDropdown yearDropdownItemNumber={10} calendarStartDay={1} popperClassName="custom-datepicker" />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 font-medium mb-1">Order number</label>
                  <input type="text" name="purchase_number" value={formData.purchase_number} onChange={handleChange} placeholder="PO-01"
                    readOnly={!!initialData}
                    className={`w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${initialData ? 'bg-gray-100 cursor-not-allowed' : ''}`} />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 font-medium mb-1">Reference</label>
                  <input type="text" name="reference" value={formData.reference} onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div className="flex gap-2.5 items-stretch">
                  <div className="flex-1" />
                  <button type="button" onClick={handlePreview}
                    className="self-end mb-px px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-md bg-white cursor-pointer hover:bg-blue-50 transition-colors">
                    Preview
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-[200px] gap-4 mb-2.5">
                <div>
                  <label className="block text-[13px] text-gray-700 font-medium mb-1">Tax</label>
                  <SimpleDropdown value={formData.tax_inclusion || 'tax_exclusive'}
                    options={[{ value: 'tax_inclusive', label: 'Tax inclusive' }, { value: 'tax_exclusive', label: 'Tax exclusive' }, { value: 'no_tax', label: 'No tax' }]}
                    onSelect={(v) => handleChange({ target: { name: 'tax_inclusion', value: v } })} />
                </div>
              </div>
            </div>

            {/* Line Items Table */}
            <div className="bg-white rounded-lg border border-gray-200 mb-8 overflow-hidden font-[Arial]">
              <table className="w-full border-collapse">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2.5 text-xs font-semibold text-gray-700 border-b border-gray-200 text-left w-10">#</th>
                    <th className="p-2.5 text-xs font-semibold text-gray-700 border-b border-gray-200 text-left w-60">Item</th>
                    <th className="p-2.5 text-xs font-semibold text-gray-700 border-b border-gray-200 text-left">Description</th>
                    <th className="p-2.5 text-xs font-semibold text-gray-700 border-b border-gray-200 text-right w-20">Qty</th>
                    <th className="p-2.5 text-xs font-semibold text-gray-700 border-b border-gray-200 text-right w-28">Unit Price</th>
                    <th className="p-2.5 text-xs font-semibold text-gray-700 border-b border-gray-200 text-left w-28">Tax Rate</th>
                    <th className="p-2.5 text-xs font-semibold text-gray-700 border-b border-gray-200 text-right w-28">Tax Amount</th>
                    <th className="p-2.5 text-xs font-semibold text-gray-700 border-b border-gray-200 text-right w-28">Amount</th>
                    <th className="p-2.5 text-xs font-semibold text-gray-700 border-b border-gray-200 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((item, idx) => (
                    <tr key={item.id}>
                      <td className="p-2 border-b border-gray-100 text-center text-gray-500">{idx + 1}</td>
                      <td className="p-2 border-b border-gray-100">
                        <SearchableItemSelect value={item.item_id || ''} items={items}
                          onSelect={(sel) => handleLineChange(item.id, 'item_id', sel.item_id, sel)} />
                      </td>
                      <td className="p-2 border-b border-gray-100">
                        <input type="text" value={item.item_description || ''} onChange={(e) => handleLineChange(item.id, 'item_description', e.target.value)}
                          className="w-full px-1 py-1 text-sm border-none focus:outline-none" />
                      </td>
                      <td className="p-2 border-b border-gray-100">
                        <input type="number" min="1" value={item.quantity} onChange={(e) => handleLineChange(item.id, 'quantity', e.target.value)}
                          className="w-full px-1 py-1 text-sm text-right border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
                      </td>
                      <td className="p-2 border-b border-gray-100">
                        <input type="number" min="0" step="0.01" value={item.unit_price} onChange={(e) => handleLineChange(item.id, 'unit_price', e.target.value)}
                          className="w-full px-1 py-1 text-sm text-right border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
                      </td>
                      <td className="p-2 border-b border-gray-100">
                        <SimpleDropdown value={item.tax_rate_id || ''}
                          options={[{ value: '', label: 'No tax' }, ...taxes.map((t) => ({ value: t.tax_id, label: `${t.tax_id} (${t.tax_rate}%)` }))]}
                          onSelect={(v) => handleLineChange(item.id, 'tax_rate_id', v)} />
                      </td>
                      <td className="p-2 border-b border-gray-100 text-right text-gray-500">
                        {formData.tax_inclusion !== 'no_tax' && item.tax_rate_id
                          ? calculateLineTax(item, taxes, formData.tax_inclusion).toFixed(2) : ''}
                      </td>
                      <td className="p-2 border-b border-gray-100 text-right font-medium">{(item.total_price || 0).toFixed(2)}</td>
                      <td className="p-2 border-b border-gray-100 text-center">
                        <button type="button" onClick={() => removeLine(item.id)}
                          className="bg-transparent border-none cursor-pointer text-red-500 hover:text-red-700" title="Remove line">🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-3 flex gap-2.5 border-t border-gray-200">
                <button type="button" onClick={addLine} className="px-3 py-1.5 text-sm font-medium bg-gray-100 border border-gray-300 rounded cursor-pointer hover:bg-gray-200 transition-colors">Add line item</button>
                <button type="button" onClick={clearLines} className="px-3 py-1.5 text-sm font-medium bg-white border border-gray-300 rounded cursor-pointer hover:bg-gray-50 transition-colors">Clear all lines</button>
              </div>
            </div>

            {/* Delivery Section */}
            <div className="grid grid-cols-[1fr_300px] gap-10 font-[Arial] mb-8">
              <div className="flex flex-col gap-4">
                <div>
                  <label className="block text-[13px] text-gray-700 font-medium mb-1">Delivery address</label>
                  <input type="text" name="ship_to" value={formData.ship_to} onChange={handleChange} placeholder="e.g. 2b Young St, PARRAMATTA, NSW"
                    className="w-full max-w-[500px] px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div className="flex gap-4">
                  <div>
                    <label className="block text-[13px] text-gray-700 font-medium mb-1">Contact name</label>
                    <input type="text" name="contact_name" value={formData.contact_name} onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-[13px] text-gray-700 font-medium mb-1">Telephone</label>
                    <input type="text" name="telephone" value={formData.telephone} onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-[13px] text-gray-700 font-medium mb-1">Email</label>
                    <input type="email" name="supplier_email" value={formData.supplier_email} onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 font-medium mb-1">Delivery instructions</label>
                  <textarea name="note_to_supplier" value={formData.note_to_supplier} onChange={handleChange}
                    className="w-full max-w-[500px] min-h-[80px] px-3 py-2 text-sm border border-gray-300 rounded font-[Arial] focus:outline-none focus:ring-1 focus:ring-blue-500" placeholder="e.g. delivery asap. Thank you" />
                </div>
              </div>
              <div className="flex flex-col gap-3 items-end pt-5">
                <div className="flex justify-between w-full text-sm"><span className="text-gray-500">Subtotal</span><span className="font-medium">{subtotal.toFixed(2)}</span></div>
                {formData.tax_inclusion !== 'no_tax' && taxTotal > 0 && (
                  <div className="flex justify-between w-full text-sm"><span className="text-gray-500">Total GST on Expenses</span><span className="font-medium">{taxTotal.toFixed(2)}</span></div>
                )}
                <div className="w-full h-px bg-gray-200 my-2.5" />
                <div className="flex justify-between w-full text-lg font-bold"><span>Total</span><span>{grandTotal.toFixed(2)}</span></div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-8 py-4 border-t border-gray-200 bg-white flex items-center gap-2.5">
        <button type="button" onClick={handleSend} className="px-6 py-2.5 bg-emerald-500 text-white rounded-md font-semibold text-sm cursor-pointer hover:bg-emerald-600 transition-colors">Approve and Send</button>
          {initialData && onImportToBill && (
            <button type="button" onClick={handleImportToBill}
              className="px-5 py-2.5 text-sm font-medium text-indigo-600 border border-indigo-600 rounded-md bg-white cursor-pointer hover:bg-indigo-50 transition-colors">
              Import to Bill
            </button>
          )}
          <div className="flex-1" />
          <button type="submit" form="purchaseForm" className="px-6 py-2.5 bg-emerald-500 text-white rounded-md font-semibold text-sm cursor-pointer hover:bg-emerald-600 transition-colors">Save</button>
          <button type="button" onClick={onClose} className="px-5 py-2.5 bg-white border border-gray-300 rounded-md font-medium text-sm cursor-pointer hover:bg-gray-50 transition-colors">Cancel</button>
        </div>
      </div>
    </div>
  );
}
